import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { Hashtag } from "../../../../cores/socialPost/interfaces/api/get-hashtags";

// Datos mock de hashtags populares para mercado hispanohablante y Chile
const POPULAR_HASHTAGS = [
  // Hashtags generales populares en español
  { name: "#amor", count: "45.2m", category: "general" },
  { name: "#vida", count: "38.7m", category: "general" },
  { name: "#feliz", count: "32.1m", category: "general" },
  { name: "#blessed", count: "28.9m", category: "general" },
  { name: "#grateful", count: "25.4m", category: "general" },
  
  // Chile específicos
  { name: "#chile", count: "12.8m", category: "general" },
  { name: "#santiago", count: "8.3m", category: "general" },
  { name: "#chileno", count: "6.7m", category: "general" },
  { name: "#valparaiso", count: "4.2m", category: "travel" },
  { name: "#atacama", count: "3.8m", category: "nature" },
  { name: "#patagonia", count: "3.5m", category: "nature" },
  { name: "#torresdelpaine", count: "2.9m", category: "nature" },
  
  // Fotografía y arte
  { name: "#fotografia", count: "15.6m", category: "photography" },
  { name: "#arte", count: "12.3m", category: "photography" },
  { name: "#fotodeldia", count: "9.8m", category: "photography" },
  { name: "#instagood", count: "18.2m", category: "photography" },
  { name: "#picoftheday", count: "14.7m", category: "photography" },
  
  // Moda y estilo
  { name: "#moda", count: "22.4m", category: "fashion" },
  { name: "#estilo", count: "18.9m", category: "fashion" },
  { name: "#outfit", count: "16.3m", category: "fashion" },
  { name: "#fashion", count: "28.7m", category: "fashion" },
  { name: "#style", count: "21.5m", category: "fashion" },
  
  // Comida y gastronomía
  { name: "#comida", count: "19.7m", category: "food" },
  { name: "#food", count: "35.2m", category: "food" },
  { name: "#foodie", count: "13.8m", category: "food" },
  { name: "#chileanfood", count: "2.1m", category: "food" },
  { name: "#empanadas", count: "1.8m", category: "food" },
  { name: "#pisco", count: "1.5m", category: "food" },
  { name: "#asado", count: "3.2m", category: "food" },
  
  // Viajes y turismo
  { name: "#viaje", count: "16.8m", category: "travel" },
  { name: "#travel", count: "42.1m", category: "travel" },
  { name: "#vacaciones", count: "8.9m", category: "travel" },
  { name: "#adventure", count: "12.4m", category: "travel" },
  { name: "#explore", count: "15.6m", category: "travel" },
  
  // Naturaleza y paisajes
  { name: "#naturaleza", count: "11.3m", category: "nature" },
  { name: "#nature", count: "29.8m", category: "nature" },
  { name: "#paisaje", count: "7.6m", category: "nature" },
  { name: "#montaña", count: "5.4m", category: "nature" },
  { name: "#oceano", count: "6.8m", category: "nature" },
  { name: "#atardecer", count: "8.2m", category: "nature" },
  
  // Entretenimiento y cultura
  { name: "#musica", count: "14.7m", category: "entertainment" },
  { name: "#music", count: "31.5m", category: "entertainment" },
  { name: "#baile", count: "6.3m", category: "entertainment" },
  { name: "#dance", count: "18.9m", category: "entertainment" },
  { name: "#cultura", count: "4.7m", category: "entertainment" },
  
  // Negocios y emprendimiento
  { name: "#emprendimiento", count: "3.8m", category: "business" },
  { name: "#negocio", count: "5.2m", category: "business" },
  { name: "#emprendedor", count: "2.9m", category: "business" },
  { name: "#marketing", count: "7.4m", category: "business" },
  
  // Tecnología y digital
  { name: "#tecnologia", count: "6.1m", category: "business" },
  { name: "#digital", count: "4.8m", category: "business" },
  { name: "#innovacion", count: "3.2m", category: "business" },
  
  // Deportes (populares en Chile)
  { name: "#futbol", count: "8.7m", category: "sports" },
  { name: "#deporte", count: "5.9m", category: "sports" },
  { name: "#tennis", count: "2.3m", category: "sports" },
  { name: "#running", count: "4.1m", category: "sports" },
];

const HASHTAG_CATEGORIES = [
  { id: "all", name: "Todos", icon: "hashtag" as IconName },
  { id: "general", name: "General", icon: "star" as IconName },
  { id: "photography", name: "Fotografía", icon: "camera" as IconName },
  { id: "fashion", name: "Moda", icon: "tshirt" as IconName },
  { id: "food", name: "Comida", icon: "utensils" as IconName },
  { id: "travel", name: "Viajes", icon: "plane" as IconName },
  { id: "nature", name: "Naturaleza", icon: "leaf" as IconName },
  { id: "entertainment", name: "Entretenimiento", icon: "music" as IconName },
  { id: "business", name: "Negocios", icon: "briefcase" as IconName },
  { id: "sports", name: "Deportes", icon: "futbol" as IconName },
];

interface Props {
  onClose: () => void;
}

export default function HashtagsSearchForm({ onClose }: Props) {
  const { company } = useMeCore();
  const {
    hashtags,
    deleteHashtag,
    addHashtag,
    updateMentions,
    inputCursorIndex,
  } = useSocialPostCore();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<typeof POPULAR_HASHTAGS>([]);

  // Hashtags del usuario (existentes)
  const userHashtags = useMemo(() => [
    { id: '', name: "#wembii", always: true, count: "0", category: "brand" },
    ...hashtags.map((hashtag) => ({ 
      ...hashtag, 
      always: false, 
      count: "0", 
      category: "user" 
    }))
  ], [hashtags]);

  // Función para simular búsqueda de hashtags
  const searchHashtags = useCallback(async (term: string, category: string) => {
    setIsSearching(true);
    
    // Simular delay de API
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let results = POPULAR_HASHTAGS;
    
    // Filtrar por categoría
    if (category !== "all") {
      results = results.filter(hashtag => hashtag.category === category);
    }
    
    // Filtrar por término de búsqueda
    if (term.trim()) {
      results = results.filter(hashtag => 
        hashtag.name.toLowerCase().includes(term.toLowerCase())
      );
    }
    
    setSearchResults(results);
    setIsSearching(false);
  }, []);

  // Efecto para buscar cuando cambia el término o categoría
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchHashtags(searchTerm, selectedCategory);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory, searchHashtags]);

  // Cargar hashtags populares al inicio
  useEffect(() => {
    searchHashtags("", "all");
  }, [searchHashtags]);

  const handleRemoveHashtag = useCallback((hashtag: Hashtag) => {
    deleteHashtag(company?.id!, hashtag.id);
  }, [deleteHashtag, company]);

  const handleAddHashtagToText = useCallback((hashtagName: string) => {
    addHashtag(hashtagName);
    updateMentions(hashtagName.length + 1, inputCursorIndex);
  }, [inputCursorIndex, addHashtag, updateMentions]);

  const formatCount = (count: string) => {
    return count;
  };

  const isHashtagAdded = (hashtagName: string) => {
    return userHashtags.some(hashtag => hashtag.name === hashtagName);
  };

  return (
    <Container className="h-100 hashtag-search-container" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div className="d-flex flex-column h-100 p-3">
            {/* Header */}
            <div className="mb-4">
              <h2 className="h2 text-center mb-3">
                Buscador de hashtags
              </h2>
              <p className="text-center text-muted small mb-0">
                Haz clic sobre un hashtag para añadirlo. Los hashtags ya añadidos se muestran en gris y se listan por orden de uso.
              </p>
            </div>

            {/* Hashtags del usuario */}
            <div className="mb-4">
              <h6 className="mb-3">Tus hashtags</h6>
              <div className="w-100 p-3 d-flex justify-content-start flex-wrap">
                {userHashtags.map((hashtag) => (
                    <Badge 
                      key={hashtag.id}
                      color="primary"
                      className="hashtag-badge text-light font-size-2 m-2 d-flex align-items-center pointer"
                      pill
                      style={{ opacity: 1 }}
                    >
                    <span onClick={() => handleAddHashtagToText(hashtag.name)}>
                      {hashtag.name}
                    </span>

                    {!hashtag.always && (
                      <FontAwesomeIcon
                        icon={["fas", "times"]}
                        size="1x"
                        color="white"
                        className="m-1 z-index-2 ml-2"
                        onClick={() => handleRemoveHashtag(hashtag)}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Barra de búsqueda */}
            <div className="mb-3">
              <div className="position-relative">
                <FontAwesomeIcon
                  icon={["fas", "search"]}
                  className="position-absolute"
                  style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999" }}
                />
                <Input
                  type="text"
                  placeholder="Escribe para buscar hashtags"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-5 search-input"
                />
                {isSearching && (
                  <Spinner size="sm" className="position-absolute" style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }} />
                )}
              </div>
            </div>

            {/* Filtros por categoría */}
            <div className="mb-3 overflow-x-auto">
              <div className="d-flex flex-wrap gap-2 category-filter" style={{ minHeight: "40px" }}>
                {HASHTAG_CATEGORIES.map((category) => (
                  <Button
                    key={category.id}
                    size="sm"
                    color={selectedCategory === category.id ? "primary" : "secondary"}
                    outline={selectedCategory !== category.id}
                    className="d-flex align-items-center flex-shrink-0"
                    onClick={() => setSelectedCategory(category.id)}
                    style={{ 
                      borderRadius: "20px",
                      minWidth: "fit-content",
                      padding: "6px 12px",
                      fontSize: "14px"
                    }}
                  >
                    <FontAwesomeIcon icon={["fas", category.icon]} className="me-2" />
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Lista de hashtags */}
            <div className="flex-grow-1 overflow-auto" style={{ maxHeight: "400px" }}>
              {searchResults.map((hashtag, index) => (
                <div
                  key={index}
                  className={`hashtag-item d-flex justify-content-between align-items-center p-3 border-bottom cursor-pointer ${
                    isHashtagAdded(hashtag.name) ? 'added' : ''
                  }`}
                  onClick={() => !isHashtagAdded(hashtag.name) && handleAddHashtagToText(hashtag.name)}
                  style={{
                    opacity: isHashtagAdded(hashtag.name) ? 0.6 : 1,
                    cursor: isHashtagAdded(hashtag.name) ? 'default' : 'pointer'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <span className="fw-bold me-2">{hashtag.name}</span>
                    {isHashtagAdded(hashtag.name) && (
                      <Badge color="secondary" size="sm">Añadido</Badge>
                    )}
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="hashtag-count text-muted me-2">{formatCount(hashtag.count)}</span>
                    {isHashtagAdded(hashtag.name) ? (
                      <FontAwesomeIcon icon={["fas", "check"]} color="#28a745" />
                    ) : (
                      <FontAwesomeIcon icon={["fas", "plus"]} color="#007bff" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Botones de acción */}
            <div className="mt-3 d-flex gap-2">
              <Button
                color="secondary"
                outline
                className="flex-grow-1"
                onClick={onClose}
              >
                Cerrar
              </Button>
              <Button
                color="primary"
                className="flex-grow-1"
                onClick={onClose}
              >
                Crear nuevo
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
